import { useCallback, useEffect, useState } from 'react'

interface OverflowIndicators<T extends HTMLElement> {
  ref: (node: T | null) => void
  showTopShadow: boolean
  showBottomShadow: boolean
}

export function useOverflowScrollIndicator<
  T extends HTMLElement,
>(): OverflowIndicators<T> {
  const [element, setElement] = useState<T | null>(null)
  const [showTopShadow, setShowTopShadow] = useState(false)
  const [showBottomShadow, setShowBottomShadow] = useState(false)

  const ref = useCallback((node: T | null) => {
    setElement(node)
  }, [])

  useEffect(() => {
    if (!element) return

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = element

      const hasOverflow = scrollHeight > clientHeight

      if (!hasOverflow) {
        setShowTopShadow(false)
        setShowBottomShadow(false)
        return
      }

      const atTop = scrollTop <= 0
      const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight

      setShowTopShadow(!atTop)
      setShowBottomShadow(!atBottom)
    }

    update()

    const handleScroll = () => update()

    element.addEventListener('scroll', handleScroll)

    // ResizeObserver to catch content/size changes
    const resizeObserver = new ResizeObserver(() => {
      update()
    })
    resizeObserver.observe(element)

    return () => {
      element.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [element])

  return { ref, showTopShadow, showBottomShadow }
}
